<template>
    <div class="home">
        <b-container fluid>
            <b-row>
              <b-col>
                <h1 v-if="!isReadonly">I & E Applicant Screening</h1>
                <h1 v-if="isReadonly">Application Details</h1>
              </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="12">
                    <p style="font-weight:750;" class="mb-1">Notes</p>
                    <b-table hover small show-empty class="sr-caption"
                             aria-live="polite"
                             responsive="true"
                             :fields="noteFields"
                             :items="application.notes"
                             :busy="false"
                             label-sort-asc=""
                             label-sort-desc=""
                             caption="Notes table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                        <template v-slot:no-data>
                            No Notes Found.
                        </template>
                        <div slot="table-busy" class="text-center text-danger my-2">
                            <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                        <template slot="empty" slot-scope="scope">
                            No Notes Found.
                        </template>
                    </b-table>
                </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="12">
                    <p style="font-weight:750;" class="mb-1">Notes Sent to/from Applicant</p>
                    <b-table hover small show-empty class="sr-caption"
                             aria-live="polite"
                             responsive="true"
                             :fields="extNoteFields"
                             :items="application.externalNotes"
                             :busy="false"
                             label-sort-asc=""
                             label-sort-desc=""
                             caption="Notes sent to/from applicant table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                        <template v-slot:no-data>
                            No Notes Found.
                        </template>
                        <div slot="table-busy" class="text-center text-danger my-2">
                            <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                        <template slot="empty" slot-scope="scope">
                            No Notes Found.
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <div class="card">
              <div class="card-header">Application Review</div>
              <div class="card-body">
                <b-row>
                    <b-col cols="12">
                        <b-button variant="secondary" size="sm" :to="{name: 'appHistory', params: { id: application.applicationId }}" role="button"
                                  v-if="hasHistory()">View Application History</b-button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <p style="font-weight:750;" class="mb-1">Application Status</p>
                        {{application.applicationStatus}}
                    </b-col>
                    <b-col cols="3" v-if="application.applicationStatus === 'WITHDRAW'">
                        <p class="mb-1"><span class="font-weight-bold">Withdraw Date: </span></p>
                        {{application.statusDate}}
                    </b-col>
                    <b-col cols="3">
                        <p style="font-weight:750;" class="mb-1">Payment Status</p>
                        {{application.paymentStatus}}
                    </b-col>
                    <b-col cols="3">
                        <p style="font-weight:750;" class="mb-1">S/S Affirm Status</p>
                        {{application.affirmStatus}}
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <p class="mb-1"><span class="font-weight-bold">Application Reopened: </span>
                            {{application.edited && application.returnForReview ? "Reopened" : application.edited && !application.returnForReview ? "Resubmitted" : ""}}</p>
                    </b-col>
                    <b-col cols="3">
                        <p style="font-weight:750;" class="mb-1">Reclassify from Temporary</p>
                        {{application.reclassify != null  ? "Yes: #" + application.reclassify : "No"}}
                    </b-col>
                </b-row>
                <b-row v-if="!isReadonly">
                  <b-col cols="12">
                    <BaseParagraphInput v-model="comments" 
                        :v="$v.comments"
                        label="Internal Review Comments"
                        slug="reviewComments"
                        tip=""
                        setId="reviewComments"/>
                  </b-col>
                </b-row>
                  
                  <b-row v-if="!isReadonly">
                    <b-col cols="12">
                        <b-dropdown text="Screening Decision" variant="primary" class="m-2">
                          <b-dropdown-item-button @click="showClearModal">Clear Applicant</b-dropdown-item-button>
                          <b-dropdown-item-button @click="showFlagModal">Flag Applicant</b-dropdown-item-button>
                          <b-dropdown-item-button @click="showHoldModal" v-if="application.screeningStatus !=='HOLD'">Hold Applicant</b-dropdown-item-button>
                        </b-dropdown>

                        <b-button variant="link" class="mr-4" @click="save">Save</b-button>
                    </b-col>
                  </b-row>
              </div>
            </div>

            <div class="card">
              <div class="card-header">Applicant Information</div>
                <div class="card-body">
                  <b-row class="mb-3">
                      <b-col cols="4" v-if="application.applicationStatus === 'WITHDRAW'">
                          <p class="mb-1"><span class="font-weight-bold">Application Status: </span></p>
                              {{application.applicationStatus}}
                      </b-col>
                      <b-col cols="4" v-if="application.applicationStatus === 'WITHDRAW'">
                          <p class="mb-1"><span class="font-weight-bold">Withdraw Date: </span></p>
                              {{application.statusDate}}
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="4"><BaseTextOutput label="Last Name" :value="application.lastName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="First Name" :value="application.firstName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Middle Name" :value="application.middleName"/></b-col>
                  </b-row>

                  <Address :value="application.address" :international="application.international"/>

                  <b-row class="mt-3">
                      <b-col cols="4"><BaseTextOutput label="Preferred Phone" :value="application.preferredPhone"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Email Address" :value="application.emailAddress"/></b-col>
                  </b-row>

                  <b-row>
                      <b-col cols="4"><BaseTextOutput label="Date of Birth" :value="application.license.dateOfBirth"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="SSN (last 4)"
                        :value="application.license.socialSecurityNumber.substr(application.license.socialSecurityNumber.length-4, 4)"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="License Number" :value="application.license.licenseNbr"/></b-col>
                  </b-row>

                  <h2 class="mt-3">Other Names</h2>
                  <OtherNames :value="application.otherNames"/>

                  <h2 class="mt-3">Trade Names</h2>
                  <TradeNames :value="application.tradeNames"/>

                  <div v-if="application.licenseSubCategory === 'FULL'">
                      <h2 class="mt-3">Other Licenses</h2>
                      <OtherLicenses :value="application.otherLicenses"/>
                  </div>

                  <h2 class="mt-4">Attachments</h2>
                  <AttachmentList :value="application.attachments"/>

                  <h2 class="mt-4">Internal Attachments</h2>
                  <AttachmentList :value="application.internalAttachments" :internal="true"/>

                </div>
            </div>

            <div class="card" v-if="!isReadonly">
                <div class="card-header" id="intAttachmentsDesc">Upload Attachments</div>
                <div class="card-body">
                    <FileAttachmentInternal v-on:attachment-added="addAttachment" :appId="application.applicationId"
                                            describedby="intAttachmentsDesc"/>
                </div>
            </div>

            <div class="card" v-if="application.subCategory != 'FULL'">
                <div class="card-header">Minnesota Licensed {{ssLabel}}(s) & Establishment(s)</div>
                <div class="card-body">
                    <div v-if="application.mnSupervisor !== null && application.mnSupervisor.length > 0">
                        <div v-for="(supervisor, index) in application.mnSupervisor" :key="supervisor.id"
                             v-bind:class="{'border-bottom':index !== application.mnSupervisor.length-1, 'mt-3': index !== 0}">
                            <b-row>
                                <b-col cols="6">
                                    <BaseTextOutput label="Supervisor" :value="supervisor.formattedAffirmation"/>
                                </b-col>
                                <b-col cols="6" v-if="application.subCategory === 'GUEST'">
                                    <BaseTextOutput label="Work Dates" :value="supervisor.workDate != null ? supervisor.workDate.startDate + ' thru ' + supervisor.workDate.endDate : ''"/>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col cols="2"><BaseTextOutput label="Received Date" :value="supervisor.receivedDate"/></b-col>
                                <b-col cols="4"><BaseTextOutput label="Establishment Name" :value="supervisor.establishment.supervisedEstName"/></b-col>
                                <b-col cols="6"><BaseTextOutput label="Establishment Address" :value="supervisor.establishment.supervisedEstFullAddress"/></b-col>
                            </b-row>
                            <b-row>
                                <b-col cols="4"><BaseTextOutput label="Establishment Phone" :value="supervisor.establishment.supervisedEstPhoneNumber"/></b-col>
                                <b-col cols="4"><BaseTextOutput label="Establishment Website" :value="supervisor.establishment.supervisedEstWebsite"/></b-col>
                                <b-col cols="4">
                                    <label style="margin-bottom:1px;"><strong>Establishment Hours</strong></label>
                                    <b-row v-for="hrs in supervisor.establishment.establishmentHours" :key="hrs.id">
                                        <b-col cols="12">
                                            {{hrs.days.map(d => d.description).join(', ') + ': ' + hrs.hours}}
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </b-row>
                        </div>
                    </div>
                    <b-row  v-if="!application.mnSupervisor || application.mnSupervisor.length === 0">
                        <b-col>
                            No supervisor provided.
                        </b-col>
                    </b-row>
                </div>
            </div>

            <div class="card" v-if="application.subCategory === 'FULL'">
                <div class="card-header">Employment History</div>
                <div class="card-body">
                    <Employers :value="application.employers"/>
                </div>
            </div>

            <div class="card">
                <div class="card-header">Applicant Questionnaire</div>
                <div class="card-body">
                    <div v-if="application.subCategory === 'FULL' && application.applicationType.code === 'R'">
                        <b-row class="mb-2">
                            <b-col cols="auto"><p>{{application.adverseActionTaken === 'Y' ? 'Yes' : 'No'}}</p></b-col>
                            <b-col cols="11">Since the date of your last application, is action being taken against you or has action been taken against you or your legal authorization to practice body art in this or any other jurisdiction either through denial of application, revocation, suspension, restrictions, limitations, conditions, reprimand, civil penalty, or any other means (including Stipulation and Consent Orders and Determinations)?</b-col>

                            <template v-if="application.adverseActionText">
                                <b-col cols="1"></b-col>
                                <b-col cols="11"><i>{{application.adverseActionText}}</i></b-col>
                            </template>
                        </b-row>
                        <b-row class="mb-2">
                            <b-col cols="auto">{{application.conviction === 'Y' ? 'Yes' : 'No'}}</b-col>
                            <b-col cols="11">Have you been convicted, within the last two years, of a felony or misdemeanor which relates to the body arts or which involved an essential element of dishonesty?</b-col>

                            <template v-if="application.convictionText">
                                <b-col cols="1"></b-col>
                                <b-col cols="11"><i>{{application.convictionText}}</i></b-col>
                            </template>
                        </b-row>
                    </div>
                    <div v-if="application.subCategory !== 'FULL' || application.applicationType.code === 'I'">
                        <QuestionResponses :value="application.questionResponses" />
                    </div>
                </div>
            </div>

            <div class="card" v-if="application.additionalInformation">
                <div class="card-header">Additional Information</div>
                <div class="card-body">
                    {{application.additionalInformation}}
                </div>
            </div>
        </b-container>
        <b-modal id="clear-modal" hide-header hide-footer>
            <h2>Clear Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="clear">
                You are about to clear the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to clear or Cancel to return to the application screening.</p>
                <div>
                    <b-button @click="closeClearModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="flag-modal" hide-header hide-footer>
            <h2>Flag Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="flag">
                You are about to flag the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to flag or Cancel to return to the application screening.</p>
                <div>
                    <b-button @click="closeFlagModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="hold-modal" hide-header hide-footer>
            <h2>Hold Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="hold">
                You are about to hold the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                Credentialing will not be able to process the application until the status is changed to "Flag" or "Clear".
                <p class="mt-2">Press Confirm to hold or Cancel to return to the application screening.</p>
                <div>
                    <b-button @click="closeHoldModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
import { required, maxLength } from 'vuelidate/lib/validators'
import Address from '@/components/templates/Address.vue'
import OtherNames from '@/components/templates/OtherNames.vue'
import TradeNames from '@/components/templates/TradeNames.vue'
import OtherLicenses from '@/components/templates/OtherLicenses.vue'
import AttachmentList from '@/components/templates/AttachmentList.vue'
import Employers from '@/components/templates/Employers.vue'
import QuestionResponses from '@/components/templates/QuestionResponses.vue'
import FileAttachmentInternal from '@/components/templates/input/FileAttachmentInternal.vue'
import moment from 'moment'

export default {
  components: {
    Address,
    OtherNames,
    OtherLicenses,
    TradeNames,
    AttachmentList,
    Employers,
    QuestionResponses,
    FileAttachmentInternal,
  },

  data: () => ({
    application: {},
    comments: null,
    noteFields: [
        {key: 'note', sortable: true, label: 'Note'},
        {key: 'noteType.description', sortable: true, label: 'Note Type'},
        {key: 'noteDate', sortable: true, label: 'Note Date'},
        {key: 'noteByName', sortable: true, label: 'Note By'}
    ],
    extNoteFields: [
        {key: 'note', sortable: true, label: 'Note'},
        {key: 'noteType.description', sortable: true, label: 'Note Type'},
        {key: 'noteDate', sortable: true, label: 'Note Date'},
        {key: 'noteByName', sortable: true, label: 'Note By'}
    ],
    isReadonly: true,
  }),
  
  mounted() {
    this.loadApplication()
  },

  validations: {
    comments: { required: false, maxLength: maxLength(4000) }
  },

  computed: {
      ssLabel() {
          if (this.application.subCategory === 'GUEST') {
              return "Sponsor"
          } else {
              return "Supervisor"
          }
      }
  },

  methods: {
    loadApplication() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/${this.$route.params.id}`)
        .then(response => {
           var statuses = ["PENDING","AMENDED","PENDING SUPERVISOR","OPTIONAL","HOLD"]
          this.application = response.data
          this.isReadonly = (this.$route.name === "readonlyScreen" || !statuses.some(s => s === this.application.applicationStatus)
              || !statuses.some(s => s === this.application.screeningStatus))
        })
    },

    clear() {
      this.submitToServer('clear')
    },

    flag() {
      this.submitToServer('flag')
    },

    hold() {
        this.submitToServer('hold')
    },

    save() {
      this.submitToServer('save')
    },

    submitToServer(action) {
      const payload = {
        applicationId: this.$route.params.id,
        applicationNotes: this.comments
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/screenings/${action}`, payload)
        .then(response => { 
          if (action != 'save') {
           this.showRootToast("Application Updates", "Your screening decision has been updated.", "success")
           this.$router.push('/home')
          } else {
            this.showRootToast("Application Updates", "The comments have been updated.", "success")
            if (!this.application.notes) {
                this.application.notes = []
            }
            this.application.notes.push({
                noteType: {
                    description: "Screening (I&E)"
                },
                note: this.comments,
                noteDate: moment().format('YYYY-MM-DD'),
                noteByName: "Me"
            })
            this.comments = null
          }
      })
    },

    showClearModal() {
        this.$bvModal.show('clear-modal')
    },

    closeClearModal() {
        this.$bvModal.hide('clear-modal')
    },

    showFlagModal() {
        this.$bvModal.show('flag-modal')
    },

    closeFlagModal() {
        this.$bvModal.hide('flag-modal')
    },

    showHoldModal() {
        this.$bvModal.show('hold-modal')
    },

    closeHoldModal() {
        this.$bvModal.hide('hold-modal')
    },

    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
            title: title,
            toaster: 'b-toaster-top-center',
            solid: true,
            variant: type,
            appendToast: true,
            noAutoHide:true
          })
        this.toastFocus()
    },
    toastFocus() {
        setTimeout(() => {
            let elem = document.getElementsByClassName("toast")
            elem = elem[elem.length-1]
            elem.focus()
        }, 100)
    },
    addAttachment(value) {
        if (value.internal) {
            this.application.internalAttachments.push(value)
        } else {
            this.application.attachments.push(value)
        }
    },
    hasHistory() {
        return (this.application.prevAddress != null && this.application.prevAddress.length > 0)
            || (this.application.prevContacts != null && this.application.prevContacts.length > 0)
            || (this.application.prevMnSupervisor != null && this.application.prevMnSupervisor.length > 0)
            || (this.application.prevQuestionResponses != null && this.application.prevQuestionResponses.length > 0)
            || (this.application.prevAttachments != null && this.application.prevAttachments.length > 0)
    }
  },
  created() {
      document.title = "I & E Applicant Screening - Body Art"
  }
}
</script>

<style>
</style>